import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Download, Maximize2, Paperclip, FilePlus2, FileText, Image as ImageIcon, Play, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const CHUNK_SIZE = 4 * 1024 * 1024;
const MAX_BYTES = 100 * 1024 * 1024;
const ACCEPT = "image/*,video/*,.pdf,.doc,.docx,.txt,.csv,.rtf,.xls,.xlsx";

const fetchBlobUrl = (incidentId, index) =>
  api.get(`/incidents/${incidentId}/attachments/${index}`, { responseType: "blob" })
    .then((res) => URL.createObjectURL(res.data));

const ImageThumb = ({ incidentId, index, filename, onOpen }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let active = true;
    let objUrl = "";
    fetchBlobUrl(incidentId, index).then((u) => { objUrl = u; if (active) setUrl(u); }).catch(() => {});
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [incidentId, index]);
  if (!url) return <div className="w-full h-32 flex items-center justify-center text-muted-foreground"><ImageIcon className="w-6 h-6" /></div>;
  return (
    <button type="button" onClick={() => onOpen({ url, filename, kind: "image" })} data-testid={`open-attachment-${index}`}
      className="block w-full cursor-zoom-in">
      <img src={url} alt={filename} className="w-full h-32 object-cover transition-transform duration-300 group-hover:scale-105" />
      <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors duration-200">
        <Maximize2 className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
      </span>
    </button>
  );
};

const TileThumb = ({ Icon, label, onClick, index, loading }) => (
  <button type="button" onClick={onClick} data-testid={`open-attachment-${index}`}
    className="block w-full h-32 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-white transition-colors duration-200">
    {loading ? <Loader2 className="w-7 h-7 animate-spin" /> : <Icon className="w-7 h-7" />}
    <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
  </button>
);

const Thumb = ({ incidentId, att, onDelete, onOpen }) => {
  const { index, filename, kind } = att;
  const [loading, setLoading] = useState(false);

  const openVideo = async () => {
    setLoading(true);
    try {
      const url = await fetchBlobUrl(incidentId, index);
      onOpen({ url, filename, kind: "video" });
    } catch {
      toast.error("Could not load video");
    } finally {
      setLoading(false);
    }
  };

  const downloadDoc = async () => {
    setLoading(true);
    try {
      const url = await fetchBlobUrl(incidentId, index);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "document";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error("Could not download file");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative group border border-border bg-background overflow-hidden" data-testid={`attachment-${index}`}>
      {kind === "image" && <ImageThumb incidentId={incidentId} index={index} filename={filename} onOpen={onOpen} />}
      {kind === "video" && <TileThumb Icon={Play} label="Play video" onClick={openVideo} index={index} loading={loading} />}
      {kind === "document" && <TileThumb Icon={FileText} label="Download" onClick={downloadDoc} index={index} loading={loading} />}
      <button onClick={() => onDelete(index)} data-testid={`delete-attachment-${index}`}
        className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-[#FF3B30] z-10">
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="absolute bottom-0 inset-x-0 bg-black/70 px-2 py-1 text-[10px] font-mono text-white truncate pointer-events-none">{filename}</div>
    </div>
  );
};

const Lightbox = ({ media, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!media) return null;

  const download = () => {
    const a = document.createElement("a");
    a.href = media.url;
    a.download = media.filename || "media";
    a.click();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex flex-col animate-fade-up" data-testid="attachment-lightbox"
      onClick={onClose}>
      <div className="flex items-center justify-between px-6 py-4" onClick={(e) => e.stopPropagation()}>
        <span className="font-mono text-sm text-white/70 truncate max-w-[60%]">{media.filename}</span>
        <div className="flex items-center gap-2">
          <Button onClick={download} data-testid="lightbox-download-btn"
            className="bg-[#007AFF] hover:bg-[#0066d6] text-white rounded-sm gap-2 font-semibold h-9">
            <Download className="w-4 h-4" /> Download Original
          </Button>
          <button onClick={onClose} data-testid="lightbox-close-btn"
            className="w-9 h-9 flex items-center justify-center border border-white/20 text-white hover:bg-white/10 transition-colors duration-200">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 pt-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {media.kind === "video" ? (
          <video src={media.url} controls autoPlay className="max-w-full max-h-full shadow-2xl" data-testid="lightbox-video" />
        ) : (
          <img src={media.url} alt={media.filename} className="max-w-full max-h-full object-contain shadow-2xl" />
        )}
      </div>
    </div>
  );
};

export const AttachmentsGallery = ({ incident, onUpdated }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const attachments = incident.attachments || [];

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("File too large — 100 MB max");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      if (file.size <= CHUNK_SIZE) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/incidents/${incident.id}/attachments`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        onUpdated(data);
      } else {
        const uploadId = Array.from(crypto.getRandomValues(new Uint8Array(16)))
          .map((b) => b.toString(16).padStart(2, "0")).join("");
        const total = Math.ceil(file.size / CHUNK_SIZE);
        for (let i = 0; i < total; i++) {
          const fd = new FormData();
          fd.append("upload_id", uploadId);
          fd.append("chunk_index", String(i));
          fd.append("total_chunks", String(total));
          fd.append("filename", file.name);
          fd.append("file", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
          const { data } = await api.post(`/incidents/${incident.id}/attachments/chunk`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
          setProgress(Math.round(((i + 1) / total) * 100));
          if (i === total - 1) onUpdated(data);
        }
      }
      toast.success("Media attached");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (index) => {
    try {
      const { data } = await api.delete(`/incidents/${incident.id}/attachments/${index}`);
      onUpdated(data);
      toast.success("Removed");
    } catch {
      toast.error("Failed to remove");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2">
          <Paperclip className="w-3.5 h-3.5" /> Media <span className="text-muted-foreground/60 normal-case tracking-normal font-normal">({attachments.length})</span>
        </label>
        <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={upload} data-testid="attachment-file-input" />
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
          data-testid="upload-attachment-btn" className="rounded-sm border-border gap-2">
          <FilePlus2 className="w-4 h-4" />
          {uploading ? (progress > 0 ? `Uploading… ${progress}%` : "Uploading…") : "Upload Media"}
        </Button>
      </div>
      {uploading && progress > 0 && (
        <div className="h-1 bg-border mb-3 overflow-hidden" data-testid="media-upload-progress">
          <div className="h-full bg-[#007AFF] transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      )}
      {attachments.length === 0 ? (
        <div className="border border-dashed border-border bg-[#0A0A0A] p-6 text-center text-sm text-muted-foreground">
          No media attached. Upload photos, videos, or accompanying documents.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="attachments-grid">
          {attachments.map((a) => (
            <Thumb key={a.index} incidentId={incident.id} att={a} onDelete={remove} onOpen={setLightbox} />
          ))}
        </div>
      )}
      <Lightbox media={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
};
